'use client';

import { LocalStorageKeys } from '@/lib/local-storage';
import { nameof, removeInvalidUUID } from '@/lib/utils';
import { createZustandShallowStore } from '@/lib/zustand';
import { BaseDT } from '@/server/base/base-trpc-service';
import { useCallback, useEffect } from 'react';
import { persist } from 'zustand/middleware';

export enum CachedNamespace {
    /**Nhóm làm việc */
    WorkingGroup = 'WorkingGroup',
    /**Nhóm quyền */
    Role = 'Role',
    /**Chức vụ */
    Position = 'Position',
    /**Phòng ban */
    Department = 'Department',
    /**Lĩnh vực */
    Field = 'Field',
    /**Thông tin nhân viên của người dùng */
    EmpInfos = 'EmpInfos',
    /**Thông tin thủ tục hành chính */
    Procedure = 'Procedure',
    /**File */
    File = 'File',
    /**File group */
    FileGroup = 'FileGroup',
}

/**Hàm xử lý data caching */
type QueryFn<T = any> = (keys: string[]) => Promise<T[]>;

type CachingDataProps<T = any, U = T> = {
    /**Key riêng biệt của tường data */
    namespace: CachedNamespace;
    /**
     * Trường key custom vào nếu có. Khi lấy data ra từ cache thì dựa vào key này
     * @default ID
     */
    key?: string;
    /**
     * Hàm gọi API THEO ID
     * Từ đó mới có data để cached
     *
     * @todo Không nên sử dụng nhiều dependencies trong function này tránh việc loop
     * */
    queryFn?: QueryFn<T>;
    /**
     * Thời gian data của namespace này hợp lệ trên hệ thống
     * @default 5 phút
     *  */
    staleTime?: number;

    /**
     * Số lần thử lại nếu lấy dữ liệu của key đó bị lỗi
     */
    maxRetry?: number;

    /**
     * Chuyển đổi/mapping data để chỉ lấy những trường cần thiết lưu xuống local-storage
     * VD: 1 Object có 10 trường nhưng chỉ cần sử dụng 3 trường và lưu 3 trường đó xuống local thì mapping qua và trả ra object còn 3 trường
     * @default undefined
     */
    mapping?: (data: T) => U;
};

/**Dữ liệu được caching */
type CachingItem = {
    data: any;
    /**
     * TODO: Chưa làm
     */
    retry?: number; // số lần thử lại khi lấy dữ liệu bị lỗi
    expiredAt: number; // thời gian hết hạn của dữ liệu
};

/**Cached data trả về các cmp sử dụng */
export type CachedDataReturn<T = any> = Record<string, T | undefined>;

/**Namespace của từng đối tượng: Role, WorkGroup, ... */
type CachingState = Record<string, CachingItem>;

type CachingDataStoreProps = {
    /**var cached stored */
    cached: Record<string, CachingState | undefined>;
    /**Status loading of each namespace */
    loading: Record<string, boolean>;
    /**Status loading fo each key in namespace */
    keyLoading: Record<string, Record<string, boolean>>;
    /**Error string of each namespace */
    errors: Record<string, Error | null>;

    queryFns: Record<string, QueryFn>;

    registerQueryFn: <T = any>(
        namespace: CachedNamespace,
        queryFn: QueryFn<T>
    ) => void;

    getQueryFn: <T = any>(namespace: CachedNamespace) => QueryFn<T> | undefined;

    setCache: <T = any>(
        namespace: CachedNamespace,
        id: string,
        data: T,
        expiresAt: number
    ) => void;
    getValidCache: <T = any>(
        namespace: CachedNamespace,
        ids: string[],
        maxRetry?: number
    ) => { validIds: string[]; cachedData: Record<string, T> };
    setLoading: (namespace: CachedNamespace, isLoading: boolean) => void;
    setKeysLoading: (
        namespace: CachedNamespace,
        keys: string[],
        isLoading: boolean
    ) => void;
    setError: (namespace: CachedNamespace, error: Error | null) => void;

    /**Set lại cached sau khi khởi tạo và kiểm tra */
    setCachedAfterHydrated: (
        state: Record<string, CachingState | undefined>
    ) => void;
};

/**
 * Key mặc định để lấy dữ liệu từ cache
 */
const defaultKeyMapping = 'ID';
/**
 * Thời gian mặc định để dữ liệu được coi là hợp lệ
 */
const defaultStaleTime = 60 * 5 * 1000;
/**
 * Số lần tối đa để thử lại khi lấy dữ liệu bị lỗi
 */
const defaultMaxRetry = 3;
/**
 * Số lượng tối đa dữ liệu được lưu trong cache
 * TODO Not implement
 */
const defaultMaxSizeCached = 50;

const useCachedLocalStore = createZustandShallowStore<CachingDataStoreProps>()(
    persist(
        (set, get) => {
            return {
                cached: {},
                loading: {},
                keyLoading: {},
                errors: {},
                queryFns: {}, // Lưu trữ queryFn cho mỗi namespace

                registerQueryFn: (namespace, queryFn) => {
                    set((state) => {
                        if (!state.queryFns[namespace]) {
                            return {
                                queryFns: {
                                    ...state.queryFns,
                                    [namespace]: queryFn,
                                },
                            };
                        }
                        return state;
                    });
                },

                getQueryFn: (namespace) => {
                    return get().queryFns[namespace];
                },

                setCache: (namespace, id, data, expiredAt) => {
                    set((state) => ({
                        cached: {
                            ...state.cached,
                            [namespace]: {
                                ...state.cached[namespace],
                                [id]: {
                                    data,
                                    expiredAt,
                                    retry: !!data
                                        ? 0
                                        : (state.cached[namespace]?.[id]
                                              ?.retry ?? 0) + 1,
                                },
                            },
                        },
                    }));
                },

                getValidCache: (namespace, ids, maxRetry = defaultMaxRetry) => {
                    const now = Date.now();
                    const namespaceCache = get().cached[namespace] || {};
                    const validIds: string[] = [];
                    const cachedData: Record<string, any> = {};

                    ids.forEach((id) => {
                        const cacheItem = namespaceCache[id];
                        if (cacheItem) {
                            if (cacheItem.data && cacheItem.expiredAt > now) {
                                validIds.push(id);
                                cachedData[id] = cacheItem.data;
                            } else {
                                if (
                                    !cacheItem.data &&
                                    typeof cacheItem.retry === 'number' &&
                                    cacheItem.retry > maxRetry
                                ) {
                                    // Trường hợp lớn hơn số lần retry thì đánh là valid Cache để không cần gọi thêm cái key này nữa
                                    validIds.push(id);
                                }
                            }
                        }
                    });

                    return { validIds, cachedData };
                },

                setLoading: (namespace, isLoading) => {
                    set((state) => ({
                        loading: {
                            ...state.loading,
                            [namespace]: isLoading,
                        },
                    }));
                },

                setKeysLoading(namespace, keys, isLoading) {
                    set((st) => {
                        const vals: Record<string, boolean> = {};
                        for (let key of keys) {
                            vals[key] = isLoading;
                        }
                        return {
                            keyLoading: {
                                ...st.keyLoading,
                                [namespace]: {
                                    ...(st.keyLoading?.[namespace] ?? {}),
                                    ...vals,
                                },
                            },
                        };
                    });
                },

                setError: (namespace, error) => {
                    set((state) => ({
                        errors: {
                            ...state.errors,
                            [namespace]: error,
                        },
                    }));
                },

                setCachedAfterHydrated(st) {
                    set({ cached: st });
                },
            };
        },
        {
            // skipHydration: true,
            name: LocalStorageKeys.Cache,
            // handle clear expired data in local
            onRehydrateStorage() {
                return (state, error) => {
                    if (error) {
                        console.error(`Has error after Rehydrate Storage`);
                    } else if (state) {
                        const now = Date.now();
                        const validCache = { ...(state.cached ?? {}) };

                        // Kiểm tra các cache đã hết hạn và xóa đi
                        Object.keys(validCache).forEach((namespace) => {
                            if (validCache[namespace]) {
                                Object.keys(validCache[namespace]).forEach(
                                    (id) => {
                                        const cacheItem =
                                            validCache[namespace]![id];
                                        if (cacheItem.expiredAt < now) {
                                            // Xóa các dữ liệu đã hết hạn
                                            delete validCache[namespace]![id];
                                        }
                                    }
                                );

                                // Kiểm tra nếu namespace đã bị xóa hết tất cả các key
                                if (
                                    Object.keys(validCache[namespace])
                                        .length === 0
                                ) {
                                    // Nếu không còn key nào trong namespace, xóa cả namespace
                                    delete validCache[namespace];
                                }
                            }
                        });

                        state.setCachedAfterHydrated(validCache);
                    }
                };
            },
            partialize(state) {
                return {
                    cached: state.cached,
                };
            },
        }
    )
);

/**Hook cached local data */
export const useCachedData = <T = any>({
    namespace,
    queryFn,
    key = defaultKeyMapping,
    staleTime = defaultStaleTime,
    maxRetry = defaultMaxRetry,
    mapping,
}: CachingDataProps<T>) => {
    const {
        cached,
        getQueryFn,
        getValidCache,
        registerQueryFn,
        setCache,
        setLoading,
        keyLoading,
        setKeysLoading,
        setError,
        loading,
        errors,
    } = useCachedLocalStore();

    /**
     * Sẽ lưu lại query function đầu tiên truyền vào
     * Sau đó những nơi khác gọi sẽ ưu tiên sử dụng hàm custom hoặc không có thì gọi hàm trong caced ra sài
     */
    useEffect(() => {
        if (queryFn) {
            registerQueryFn(namespace, queryFn);
        }
    }, [namespace, queryFn, registerQueryFn]);

    const getCachedData = useCallback(
        async (keys: string[]): Promise<Record<string, T>> => {
            const fn = queryFn || getQueryFn<T>(namespace);

            if (!fn) {
                return {};
            }
            setLoading(namespace, true);
            setError(namespace, null);

            // Lọc bỏ ID trùng lặp và các key không hợp lệ
            let uniqueKeys: string[] = Array.from(new Set(keys));

            if (key === nameof<BaseDT>('ID')) {
                // Nếu key là ID thì sẽ lọc bỏ các UUID không hợp lệ
                uniqueKeys = removeInvalidUUID(uniqueKeys);
            }

            const { validIds: validKeys, cachedData } = getValidCache<T>(
                namespace,
                uniqueKeys,
                maxRetry
            );
            const invalidKeys = uniqueKeys.filter((key) => {
                return (
                    !validKeys.includes(key) && // key không nằm trong validKeys
                    !!key // key không được rỗng
                );
            });

            let fetchedData: Record<string, T> = {};
            if (invalidKeys.length > 0) {
                try {
                    setKeysLoading(namespace, invalidKeys, true);
                    const fetchedArray = await fn(invalidKeys);

                    // Convert mảng thành object
                    fetchedData = fetchedArray.reduce(
                        (acc, item) => {
                            // LUÔN DÙNG ID LÀM KEY MẶC ĐỊNH
                            // Thực hiện mapping ở đây nếu cần
                            acc[(item as any)[key]] =
                                typeof mapping === 'function'
                                    ? mapping(item)
                                    : item;
                            return acc;
                        },
                        {} as Record<string, T>
                    );

                    // Cập nhật cache
                    const now = Date.now();
                    invalidKeys.forEach((key) => {
                        if (fetchedData[key]) {
                            setCache(
                                namespace,
                                key,
                                fetchedData[key],
                                now + staleTime
                            );
                        }
                    });
                    setKeysLoading(namespace, invalidKeys, false);
                } catch (err) {
                    setError(namespace, err as Error);
                }
            }

            setLoading(namespace, false);
            return { ...cachedData, ...fetchedData };
        },
        [
            queryFn,
            getQueryFn,
            namespace,
            setLoading,
            setError,
            getValidCache,
            maxRetry,
            setKeysLoading,
            key,
            mapping,
            setCache,
            staleTime,
        ]
    );

    /**Hàm lấy data từ cached local và trả về dữ liệu theo kiểu dùng được */
    const _getCachedByNS = useCallback(
        (ns: CachedNamespace): CachedDataReturn<T> => {
            let _data: CachedDataReturn<T> = {};
            const objCached = cached[ns];

            if (objCached) {
                for (let x in objCached) {
                    _data[x] = objCached[x].data;
                }
            }

            return _data;
        },
        [cached]
    );

    return {
        data: _getCachedByNS(namespace),
        loading: loading[namespace] || false,
        keyLoading: keyLoading[namespace] || {},
        error: errors[namespace] || null,
        getCachedData,
    };
};
